import React, { useState } from "react";
import axios from "axios";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { toast } from "react-toastify"; // Added toast for error handling feedback
import { FaSearch, FaUser, FaWallet, FaSpinner } from 'react-icons/fa';
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

// Helper function for currency formatting
const formatCurrency = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    // Assuming KES for demonstration
    return new Intl.NumberFormat('en-KE', { 
        style: 'currency', 
        currency: 'KES', 
        minimumFractionDigits: 2,
    }).format(numericAmount).replace('KES', 'Ksh ');
};

const UserBalancePage = () => {
    const [userData, setUserData] = useState("");
    const [userBalance, setUserBalance] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const token = localStorage.getItem("token");

    const fetchUserBalance = () => {
        if (!userData) {
            toast.error("Please enter a User ID or identifier to search.");
            return;
        }
        if (!token) {
            toast.error("Authentication token not found.");
            return;
        }

        setIsLoading(true);
        setUserBalance(null);
        setError(null);

        axios
            .get(`${StandardURL}/iNyumba/balance/user/${userData}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            .then((response) => {
                // Assuming response.data.response is an array, take the first element as per original logic
                if (response.data.response && response.data.response.length > 0) {
                    setUserBalance(response.data.response[0]);
                } else {
                    setUserBalance(null);
                    setError(`No balance record found for identifier: ${userData}`);
                }
            })
            .catch((err) => {
                const errorMessage = err.response?.data?.response || "Failed to fetch user balance. Check the ID.";
                setError(errorMessage);
                toast.error(errorMessage);
                console.error("Error fetching user balance:", err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleInputChange = (e) => {
        setUserData(e.target.value);
    };

    const handleEnterKeyPress = (e) => {
        if (e.key === "Enter") {
            fetchUserBalance();
        }
    };

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 flex justify-center p-4 sm:p-6 lg:p-8">
                    <div className="w-full max-w-xl bg-white p-8 rounded-xl shadow-2xl border border-gray-100">
                        
                        {/* Header */}
                        <h1 className="text-3xl font-extrabold text-gray-900 mb-6 flex items-center border-b pb-4">
                            <FaWallet className="text-purple-600 mr-3" /> User Balance Lookup
                        </h1>

                        {/* Search Input and Button */}
                        <div className="mb-8 p-4 bg-purple-50 rounded-lg">
                            <label htmlFor="user-data-input" className="block text-sm font-medium text-purple-800 mb-2 flex items-center">
                                <FaUser className="mr-2" /> Enter User ID or Identifier
                            </label>
                            <div className="flex space-x-3">
                                <input
                                    type="text"
                                    id="user-data-input"
                                    value={userData}
                                    onChange={handleInputChange}
                                    onKeyPress={handleEnterKeyPress}
                                    placeholder="e.g., UserID_123 or email address"
                                    className="flex-grow border border-gray-300 rounded-lg py-2 px-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition duration-150"
                                    disabled={isLoading}
                                />
                                <button
                                    onClick={fetchUserBalance}
                                    disabled={isLoading}
                                    className={`inline-flex items-center justify-center font-bold py-2 px-4 rounded-lg shadow-md transition duration-200 text-white 
                                        ${isLoading || !userData
                                            ? "bg-purple-300 cursor-not-allowed"
                                            : "bg-purple-600 hover:bg-purple-700 focus:ring-purple-500 focus:ring-2 focus:ring-offset-2"
                                        } disabled:opacity-80`}
                                >
                                    {isLoading ? (
                                        <FaSpinner className="animate-spin w-5 h-5" />
                                    ) : (
                                        <FaSearch className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Results / Feedback */}
                        {isLoading && (
                            <div className="flex justify-center items-center py-6">
                                <Oval height={30} width={30} color="#8b5cf6" ariaLabel="loading-indicator" />
                                <p className="ml-3 text-purple-600">Searching for balance...</p>
                            </div>
                        )}

                        {error && !isLoading && (
                             <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded" role="alert">
                                <p className="font-bold">Error</p>
                                <p>{error}</p>
                            </div>
                        )}

                        {userBalance !== null && !isLoading && !error && (
                            <div className="bg-white border-4 border-purple-500 shadow-xl rounded-xl p-6 transform transition duration-500 hover:scale-[1.01]">
                                <p className="text-xl font-extrabold mb-4 text-gray-800 flex items-center">
                                    <FaWallet className="mr-3 text-purple-600" /> Current User Balance
                                </p>
                                
                                <div className="grid grid-cols-2 gap-4 border-t pt-4">
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <p className="text-sm font-medium text-gray-500">User ID</p>
                                        <p className="text-lg font-bold text-gray-900">{userBalance.user_id}</p>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <p className="text-sm font-medium text-gray-500">Account Balance</p>
                                        <p className={`text-2xl font-extrabold ${
                                            parseFloat(userBalance.balance) >= 0 ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                            {formatCurrency(userBalance.balance)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {userBalance === null && !isLoading && !error && (
                            <div className="text-center p-10 text-gray-500 border-2 border-dashed border-gray-300 rounded-xl">
                                <FaSearch className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                                <p className="text-lg">Enter a user identifier above to find their current balance.</p>
                            </div>
                        )}

                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default UserBalancePage;